import classnames from 'classnames';
import noop from 'lodash/noop';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { updateInterval } from '@store/common/actions';
import DatePicker from '@components/common/DatePicker';

import DatePickerWithOption from './DatePickerWithOptions';
import styles from './index.less';
import { calcComparisonInterval, getComparisonDisabledDate } from './utils/dates';
import { defaultCalenderBoundry } from '@src/emp-3.0/components/Sankey/constants';

const DatePickers = ({
  interval,
  comparisonInterval,
  setComparisonInterval,
  setComparisonUnavailable,
  validateUpdateInterval,
  dod,
  summaryDatePickerRef,
  comparisonDatePickerRef,
  durationAllowed,
  durationLimit,
  placement,
  shortcuts,
  showCustom = true,
  calenderBoundry = defaultCalenderBoundry,
}) => {
  const dispatch = useDispatch();

  const datePickerClassName = classnames(styles.summaryDatePicker, { dod });
  const { from, to, shortcut } = interval;
  const { from: comparisonFrom, to: comparisonTo } = comparisonInterval;
  const [summaryInterval, setSummaryInterval] = useState([from, to, shortcut]);
  const [compInterval, setCompInterval] = useState([comparisonFrom, comparisonTo]);
  const [validComparison, setValidComparison] = useState(comparisonFrom && comparisonTo);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const fixedRange = useMemo(
    () => moment(summaryInterval[1]).diff(moment(summaryInterval[0]), 'minutes'),
    [summaryInterval],
  );

  const onCancel = useCallback(
    () => {
      if (summaryDatePickerRef.current) {
        setSummaryInterval([from, to, shortcut]);
        summaryDatePickerRef.current.reset();
      }

      if (comparisonDatePickerRef.current) {
        setCompInterval([comparisonFrom, comparisonTo]);
        comparisonDatePickerRef.current.reset();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      from,
      to,
      shortcut,
      comparisonFrom,
      comparisonTo,
      comparisonDatePickerRef.current,
      summaryDatePickerRef.current,
    ],
  );

  const openSummaryDatePicker = useCallback(
    () => {
      if (comparisonDatePickerRef.current) {
        comparisonDatePickerRef.current.close();
      }
      setSummaryOpen(true);
      setComparisonOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const closeSummaryDatePicker = useCallback(() => {
    setSummaryOpen(false);
  }, []);

  const changeSummaryDatePicker = useCallback(
    ({ interval: updatedInterval, selectedShortcut }) => {
      setSummaryInterval([...updatedInterval, selectedShortcut]);
      const { from: compFrom, to: compTo } = calcComparisonInterval(
        {
          from: updatedInterval[0],
          to: updatedInterval[1],
          shortcut: selectedShortcut,
        },
        durationAllowed,
      );
      if (compFrom && compTo) {
        setCompInterval([compFrom.format(), compTo.format()]);
        if (comparisonDatePickerRef.current) {
          comparisonDatePickerRef.current.setDate([compFrom, compTo]);
        }
      } else {
        setCompInterval(['', '']);
      }
      setValidComparison(compFrom && compTo);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const changeComparisonDatePicker = useCallback(({ interval: newInterval }) => {
    setCompInterval([newInterval[0].format(), newInterval[1].format()]);
  }, []);

  const openComparisonDatePicker = useCallback(
    () => {
      summaryDatePickerRef.current.close();
      setSummaryOpen(false);
      setComparisonOpen(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const closeComparisonDatePicker = useCallback(() => {
    setComparisonOpen(false);
  }, []);

  const onApply = useCallback(
    (interval, selectedShortcut) => {
      setComparisonOpen(false);
      setSummaryOpen(false);
      validateUpdateInterval(
        [moment(summaryInterval[0]), moment(summaryInterval[1])],
        summaryInterval[2],
      );
      if (validComparison) {
        const [from, to] = interval;
        dispatch(updateInterval(from.format(), to.format(), selectedShortcut));
        setComparisonInterval(...compInterval);
      } else {
        setComparisonUnavailable();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [compInterval, summaryInterval],
  );

  return (
    <div className={styles.datePickers}>
      <DatePickerWithOption
        durationAllowed={durationAllowed}
        durationLimit={durationLimit}
        title="Time Frame"
        ref={summaryDatePickerRef}
        expanded={comparisonOpen}
        defaultValue={[moment(summaryInterval[0]), moment(summaryInterval[1])]}
        className={datePickerClassName}
        defaultShortcut={shortcut}
        onApply={onApply}
        onOpen={openSummaryDatePicker}
        onCancel={onCancel}
        onClose={closeSummaryDatePicker}
        onChange={changeSummaryDatePicker}
        placement={placement || ''}
        shortcuts={shortcuts}
        showCustom={showCustom}
        calenderBoundry={calenderBoundry}
      />
      {dod && validComparison && (
        <>
          <div className={`${styles.separator} ${summaryOpen || comparisonOpen ? 'selected' : ''}`}>
            <div className={styles.vs}>vs.</div>
          </div>
          <DatePicker
            ref={comparisonDatePickerRef}
            className={styles.comparisonDatePicker}
            title="Comparison"
            expanded={summaryOpen}
            defaultValue={[moment(compInterval[0]), moment(compInterval[1])]}
            disabledDate={getComparisonDisabledDate(summaryInterval, durationAllowed)}
            onApply={onApply}
            onOpen={openComparisonDatePicker}
            onCancel={onCancel}
            onClose={closeComparisonDatePicker}
            onChange={changeComparisonDatePicker}
            fixedRange={fixedRange}
            durationAllowed={durationAllowed}
          />
        </>
      )}
      {dod && !validComparison && <div className={styles.unavailable}>Comparison Unavailable</div>}
    </div>
  );
};

DatePickers.defaultProps = {
  setComparisonInterval: noop,
  setComparisonUnavailable: noop,
  dod: false,
  summaryDatePickerRef: {},
  validateUpdateInterval: noop,
  comparisonDatePickerRef: {},
  durationLimit: 7,
  calenderBoundry: {},
};

export default React.memo(DatePickers);
